import React from 'react';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';

import { useCustomer } from 'hooks/useCustomer';
import { fetcherWithBody } from 'services/fetcher';
import validationSchema from 'schemas/EditCellPhoneValidationSchema';
import useCurrentCountry from 'hooks/useCurrentCountry';
import ErrorMessage from 'components/Common/Formik/ErrorMessage/ErrorMessage';
import RadioFieldCustom from 'components/Common/Formik/RadioFieldCustom/RadioFieldCustom';
import TextFieldCustom from 'components/Common/Formik/TextFieldCustom/TextFieldCustom';
import { ExtraStyleType } from 'components/Common/Formik/FieldCustom/FieldCustom';
import FormikErrorsGtmLogger from 'components/Common/FormikErrorsGtmLogger/FormikErrorsGtmLogger';
import Button from 'components/Common/Button/Button';
import { apiUrls } from 'services/api-urls';
import { replacePhoneNumberWithoutSpace } from 'services/formatter';
import { GTM_FORM_NEWSLETTER } from 'settings/gtm/gtmForm';
import { GTM_PAGE_ACCOUNT } from 'settings/gtm/gtmPageView';

import styles from './FormNewsletter.module.scss';

function FormNewsletter(): JSX.Element {
  const { t } = useTranslation(['summaryPage']);
  const { isUk } = useCurrentCountry();
  const { refreshCustomer, smsOptin } = useCustomer();
  const initialValues = { smsOptionField: '', cellPhone: '' };
  const extraStyle: ExtraStyleType = {
    container: styles.textFieldContainer,
    fieldContainer: styles.fieldContainerTextField,
    label: styles.textFieldLabel,
  };

  const extraStyleRadio: ExtraStyleType = {
    container: styles.radioContainer,
    fieldContainer: styles.radioFieldContainer,
    label: styles.radioLabel,
  };

  const smsOptions = [
    {
      label: t(`LABEL_YES`, { ns: 'translation' }),
      value: 'O',
    },
    {
      label: t(`LABEL_NO`, { ns: 'translation' }),
      value: 'N',
    },
  ];

  const onSubmit = async (values: typeof initialValues) => {
    const { cellPhone, smsOptionField } = values;
    const body = {
      cellPhone: replacePhoneNumberWithoutSpace(cellPhone),
      smsOption: smsOptionField,
    };

    const response = await fetcherWithBody(apiUrls.getCustomerUpdate(), body);

    if (response.message === 'OK') {
      await refreshCustomer();
    }
  };

  return (
    <div className={styles.container}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema(isUk, t)}
        onSubmit={onSubmit}
      >
        {({ values }) => {
          return (
            <Form>
              <FormikErrorsGtmLogger
                page_type={GTM_PAGE_ACCOUNT}
                form_type={GTM_FORM_NEWSLETTER}
              />
              <TextFieldCustom
                label={t('LABEL_CELLPHONE')}
                name="cellPhone"
                type="tel"
                validation="number"
                extraStyle={extraStyle}
                isErrorVisible={false}
              />
              <ErrorMessage name="cellPhone" />
              {values.cellPhone && smsOptin === null && (
                <div className={styles.radioButtonForm}>
                  <label>{t('LABEL_NEWS_SMS_OPTION')}</label>
                  <RadioFieldCustom
                    label={t('LABEL_NEWS_VIA_SMS')}
                    name="smsOptionField"
                    required
                    options={smsOptions}
                    extraStyle={extraStyleRadio}
                    isErrorVisible={false}
                  />
                </div>
              )}
              <div className={styles.button}>
                <Button>{t('LABEL_VALID_BUTTON')}</Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}

export default FormNewsletter;
